/**
 * This is a compiled JS file.
 * Changes here could be overwritten. 
 * Contact your Shopify developers if changes need to be made.
 */

/**
 * PageState
 * 
 * Responsible for holding the a keyed "state" that components can
 * push to, consume from and listen for window level events.
 * 
 * Can optionally store state in local storage.
 */


class PageState extends HTMLElement {
  constructor() {
    super();
    this.root = 'WS';

    const localStorageState = localStorage.getItem(`${this.root}_state`);

    // Create a new store
    // Check of there is any state from local storage to add on init
    this.createStore({
      state: {
        ...(localStorageState ? { ...JSON.parse(localStorageState) } : {}),
      },
    });
  }

  /**
   * Create a new window level object to store data on
   * Also exposes methods to get/update state
   *
   * @param {object} initialState - Any items one needs in the store on init
   */
  createStore(initialState = {}) {
    window[this.root] = window[this.root] || {};

    window[this.root] = {
      ...initialState,
      updateState: this.updateState.bind(this),
      getStateByKey: this.getStateByKey.bind(this),
      removeStateFromLocalStorage: this.removeStateFromLocalStorage.bind(this),
      removeStateFromLocalStorageByKey:
        this.removeStateFromLocalStorageByKey.bind(this),
      resetGlobalState: this.resetGlobalState.bind(this),
    };
  }

  /**
   * Update state with a payload
   *
   * UPDATE
   * window.WS.updateState('UPDATE', {id: 'cat', legs: 4}, 'pets');
   *
   * UPDATE (no persist)
   * Update state without persisting to local storage
   *
   * window.WS.updateState('UPDATE', {id: 'cat', legs: 4, persist: false}, 'pets');
   *
   * REMOVE
   * window.WS.updateState('REMOVE', {id: 'cat'}, 'pets');
   *
   * @param {string} action - "UPDATE" | "REMOVE"
   * @param {object} data - Payload for the store. Must contain an `id` property
   * @param {string} key - The state key of the object you want to update
   */
  updateState(action, data, key) {
    // Validate
    try {
      if (!data.id) {
        throw new Error('No data.id property passed');
      }
    } catch (err) {
      console.error(err);
      return;
    }

    // Get values for storage
    const { id, persist = true, ...rest } = data;

    // Add a new group if one does not exist
    if (!window[this.root].state[key]) {
      window[this.root] = {
        // Spread the existing data first (could include state)
        ...window[this.root],
        // Then creaate an updated state property
        state: {
          // Spread the existing data first
          ...window[this.root].state,
          // Add an empty keyed object i.e.
          // collectionKit_298366369977: {}
          // to add/remove items to/from
          [key]: {},
        },
      };
    }

    switch (action) {
      case 'UPDATE':
        // Add copy of state with new item to local storage first
        if (persist) {
          localStorage.setItem(
            `${this.root}_state`,
            JSON.stringify({
              ...window[this.root].state,
              [key]: {
                ...window[this.root].state[key],
                [id]: {
                  ...window[this.root].state[key][id],
                  ...rest,
                },
              },
            })
          );
        }

        window[this.root] = {
          ...window[this.root],
          state: {
            ...window[this.root].state,
            [key]: {
              ...window[this.root].state[key],
              [id]: {
                ...window[this.root].state[key][id],
                ...rest,
              },
            },
          },
        };

        this.dispatch(`${this.root}_stateUpdated`, { key });

        break;
      case 'REMOVE':
        if (persist) {
          // Add copy of state with item removed to local storage first
          localStorage.setItem(
            `${this.root}_state`,
            JSON.stringify({
              ...window[this.root].state,
              [key]: {
                ...Object.entries(window[this.root].state[key]).reduce(
                  (acc, [key, value]) =>
                    key === id
                      ? acc
                      : {
                          [key]: {
                            ...value,
                          },
                          ...acc,
                        },
                  {}
                ),
              },
            })
          );
        }

        if (window[this.root].state[key][id]) {
          window[this.root] = {
            ...window[this.root],
            state: {
              ...window[this.root].state,
              [key]: {
                ...Object.entries(window[this.root].state[key]).reduce(
                  (acc, [key, value]) =>
                    key === id
                      ? acc
                      : {
                          [key]: {
                            ...value,
                          },
                          ...acc,
                        },
                  {}
                ),
              },
            },
          };
        }

        this.dispatch(`${this.root}_stateUpdated`, { key });

        break;
      default:
        return { ...window[this.root] };
        break;
    }
  }

  /**
   * Get a "group" of state by key
   *
   * state: {
   *   pets: {
   *     cat: {id: 'cat', legs: 4}
   *     ant: {id: 'ant', legs: 6}
   *   }
   * }
   *
   * Using 'pets' as a key, returns:
   * {
   *  cat: {id: 'cat', legs: 4},
   *  ant: {id: 'ant', legs: 6}
   * }
   *
   * Can also access nested objects with dot notation
   *
   * Using 'pets.cat' as a key, returns:
   *
   * {id: 'cat', legs: 4}
   *
   * @param {string} key - Key of the object to return
   * @returns null if no object with matching key found
   */
  getStateByKey(key) {
    if (!key) {
      return null;
    }

    const keys = key.split('.');

    if (keys.length === 1) {
      if (window[this.root].state[keys[0]]) {
        return { ...window[this.root].state[keys[0]] };
      }
    } else if (keys.length === 2) {
      if (
        window[this.root].state[keys[0]] &&
        window[this.root].state[keys[0]][keys[1]]
      ) {
        return { ...window[this.root].state[keys[0]][keys[1]] };
      }
    }

    return null;
  }

  /**
   * Dispatch events that other components may be listening to
   *
   * @param {string} eventName - name of the event
   * @param {object} -  must contain a "key" property with a state group id
   */
  dispatch(eventName, { key, ...rest }) {
    try {
      if (!key) {
        throw new Error('No key property passed');
      }

      switch (eventName) {
        case `${this.root}_stateUpdated`:
          document.body.dispatchEvent(
            new CustomEvent(`${this.root}_stateUpdated`, {
              detail: {
                key,
                ...rest,
              },
            })
          );
          break;
        case `${this.root}_localStorageCleared`:
          document.body.dispatchEvent(
            new CustomEvent(`${this.root}_localStorageCleared`, {
              detail: {
                key,
                ...rest,
              },
            })
          );
          break;
        case `${this.root}_resetGlobalState`:
          document.body.dispatchEvent(
            new CustomEvent(`${this.root}_resetGlobalState`, {
              detail: {
                key,
                ...rest,
              },
            })
          );
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
      return;
    }
  }

  /**
   * Remove state from local storage
   */
  removeStateFromLocalStorage() {
    window.localStorage.removeItem(`${this.root}_state`);
  }

  /**
   * Dispatch a global reset event to let listeners know
   * they should check again
   *
   * @param {object} - must contain a "key" property with a state group id
   */
  resetGlobalState({ key, ...rest }) {
    try {
      if (!key) {
        throw new Error('No key property passed');
      }

      this.dispatch(`${this.root}_resetGlobalState`, {
        key,
        ...rest,
      });
    } catch (err) {
      console.error(err);
      return;
    }
  }

  /**
   * Remove group from local storage state by key
   */
  removeStateFromLocalStorageByKey(key) {
    try {
      if (!key) {
        throw new Error('No key property passed');
      }

      if (window[this.root].state[key]) {
        const state = { ...window[this.root].state };
        delete state[key];

        window[this.root] = {
          ...window[this.root],
          state: {
            ...state,
          },
        };

        localStorage.setItem(
          `${this.root}_state`,
          JSON.stringify(window[this.root].state)
        );

        this.dispatch(`${this.root}_localStorageCleared`, {
          key,
        });
      }
    } catch (err) {
      console.error(err);
      return;
    }
  }
}

customElements.define('page-state', PageState);
